import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { WorkOrderSchema, WorkOrderCreateInputSchema, WorkOrderUpdateInputSchema, type WorkOrder, type WorkOrderCreateInput, type WorkOrderUpdateInput } from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import {
  type CountOptions,
  type ListOptions,
  runAverage,
  runCount,
  runList,
  runSum,
} from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `workOrder` (ordre de travail technique, org-wide).
 *
 * Réplique le PATRON `asset` : l'entrée est VALIDÉE par Zod
 * (`WorkOrderCreateInputSchema`) AVANT toute écriture Firestore. On n'écrit
 * jamais un document pour découvrir ensuite qu'il est invalide. Les champs
 * standard sont remplis par le SDK, les requêtes `list` sont cadrées par orgId.
 */

export type { WorkOrderCreateInput };

/** Nom de la collection Firestore de la famille workOrder. */
export const WORK_ORDERS_COLLECTION = 'workOrders';

/**
 * Repository typé pour la famille `workOrder`.
 * Toute lecture est validée par `WorkOrderSchema`, toute écriture par
 * `WorkOrderCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface WorkOrderRepository {
  create(input: WorkOrderCreateInput): Promise<WorkOrder>;
  update(id: string, patch: WorkOrderUpdateInput): Promise<WorkOrder>;
  list(options?: ListOptions): Promise<WorkOrder[]>;
  count(options?: CountOptions): Promise<number>;
  /** Somme serveur d'un champ, memes options que `count`. Cf. list-query.ts. */
  sum(field: string, options?: CountOptions): Promise<number>;
  /** Moyenne serveur d'un champ, `null` si aucun document. Cf. list-query.ts. */
  average(field: string, options?: CountOptions): Promise<number | null>;
  get(id: string): Promise<WorkOrder | null>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<WorkOrder>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<WorkOrder>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<WorkOrder>;
}

const workOrderConverter = createConverter<WorkOrder>((raw: DocumentData) => WorkOrderSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createWorkOrderRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): WorkOrderRepository {
  const col = collection(db, WORK_ORDERS_COLLECTION).withConverter(workOrderConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<WorkOrder>(db, col, WORK_ORDERS_COLLECTION, getActorId);

  return {
    async create(input: WorkOrderCreateInput): Promise<WorkOrder> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore. (Même doctrine que le patron asset.)
      const fields = WorkOrderCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` / `null` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.assetId !== undefined) optional['assetId'] = fields.assetId;
      if (fields.custodyId !== undefined) optional['custodyId'] = fields.custodyId;
      if (fields.placeId !== undefined) optional['placeId'] = fields.placeId;
      if (fields.contactId !== undefined) optional['contactId'] = fields.contactId;
      if (fields.status !== undefined) optional['status'] = fields.status;
      if (fields.diagnosis !== undefined) optional['diagnosis'] = fields.diagnosis;
      if (fields.partLines !== undefined) optional['partLines'] = fields.partLines;
      if (fields.laborMinutes !== undefined) optional['laborMinutes'] = fields.laborMinutes;
      if (fields.assignedWorkerId !== undefined) optional['assignedWorkerId'] = fields.assignedWorkerId;
      if (fields.completedAt !== undefined) optional['completedAt'] = fields.completedAt;
      if (fields.orderId !== undefined) optional['orderId'] = fields.orderId;
      if (fields.bookingId !== undefined) optional['bookingId'] = fields.bookingId;
      if (fields.ticketId !== undefined) optional['ticketId'] = fields.ticketId;
      if (fields.inspectionId !== undefined) optional['inspectionId'] = fields.inspectionId;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;
      if (fields.couleur != null) optional['couleur'] = fields.couleur;
      if (fields.photo != null) optional['photo'] = fields.photo;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as WorkOrder);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit.
      return WorkOrderSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async update(id: string, patch: WorkOrderUpdateInput): Promise<WorkOrder> {
      // Valider AVANT d'ecrire (meme doctrine que create).
      const fields = WorkOrderUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'ecriture partielle : le converter
      // exigerait le document complet.
      await updateDoc(doc(db, WORK_ORDERS_COLLECTION, id), data);

      // Relire le document reel via la ref convertie : source de verite.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`workOrder ${id} introuvable apres mise a jour.`);
      }
      return snap.data();
    },

    async list(options?: ListOptions): Promise<WorkOrder[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async sum(field: string, options?: CountOptions): Promise<number> {
      // Sommer sans charger, meme cadrage que `count` (cf. runSum,
      // list-query.ts). Rend 0 quand aucun document ne passe le filtre.
      return runSum(col, orgId, field, options);
    },

    async average(field: string, options?: CountOptions): Promise<number | null> {
      // `null` quand aucun document ne passe le filtre : c'est ce que rend
      // Firestore, et un zero mentirait (cf. runAverage, list-query.ts).
      return runAverage(col, orgId, field, options);
    },

    async get(id: string): Promise<WorkOrder | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
